import React, { useState, useEffect } from "react";
import { FaPlus, FaSave, FaEdit, FaTrash } from "react-icons/fa";
import SearchableList from "../../../../SearchableList/SearchableList";
import DomainDropdown from "../../../../DropDowns/DomainDropdown/DomainDropdown";
import EnabledDropdown from "../../../../DropDowns/EnabledDropdown/EnabledDropdown";
import Dialog from "../../../FormDialog/Dialog";
import { keepHiddenSelections } from "../../../constants/entitlementDomains";

interface PogFormProps {
  selectedPog?: any;
  permissionObjects: any[];
  pogs: any[];
  onCreate: (data: any) => void;
  onUpdate: (id: number, data: any) => void;
  onDirtyChange: (dirty: boolean) => void;
  onModeChange: (mode: "create" | "edit" | "view" | null) => void;
  onDelete: (id: number) => void;
  canDelete?: boolean;
}

const PogForm: React.FC<PogFormProps> = ({
  selectedPog,
  permissionObjects,
  pogs,
  onCreate,
  onUpdate,
  onDirtyChange,
  onModeChange,
  onDelete,
  canDelete = false,
}) => {
  const [pogName, setPogName] = useState("");
  const [selectedDomain, setSelectedDomain] = useState("");
  const [description, setDescription] = useState("");
  const [enabled, setEnabled] = useState("Y");
  const [selectedPOs, setSelectedPOs] = useState<number[]>([]);
  const [pogId, setPogId] = useState<number | null>(null);

  const [isViewMode, setIsViewMode] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const [modifiedFields, setModifiedFields] = useState<Record<string, boolean>>({});
  const [activeField, setActiveField] = useState<string | null>(null);

  const [dialog, setDialog] = useState<any>({
    open: false,
    title: "",
    message: "",
    confirmButtonText: "",
    onConfirm: () => {},
  });

  const closeDialog = () =>
    setDialog({
      open: false,
      title: "",
      message: "",
      confirmButtonText: "",
      onConfirm: () => {},
    });

  // -----------------------------------------------------
  // LOAD SELECTED POG
  // -----------------------------------------------------
  useEffect(() => {
    if (selectedPog && !isEditMode) {
      setPogName(selectedPog.pog_name || "");
      setSelectedDomain(selectedPog.domain || "");
      setDescription(selectedPog.description || "");
      setEnabled(selectedPog.enabled || "Y");
      setPogId(selectedPog.permission_object_group_id);

      const ids =
        selectedPog.permission_objects?.map(
          (po: any) => po.permission_object_id
        ) || [];

      setSelectedPOs(ids);

      setIsViewMode(true);
      onDirtyChange(false);
      onModeChange("view");
      setModifiedFields({});
    }
  }, [selectedPog]);

  // -----------------------------------------------------
  // VALIDATION
  // -----------------------------------------------------
  const validate = () => {
    if (!pogName.trim()) {
      alert("POG Name is required");
      return false;
    }

    if (selectedPOs.length === 0) {
      alert("Select at least one Permission Object");
      return false;
    }

    const duplicate = pogs.some(
      (p) =>
        p.pog_name.toLowerCase() === pogName.trim().toLowerCase() &&
        p.permission_object_group_id !== pogId
    );

    if (duplicate) {
      alert("POG Name already exists");
      return false;
    }

    return true;
  };

  // -----------------------------------------------------
  // SUBMIT
  // -----------------------------------------------------
  const handleSubmit = (e: any) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      pog_name: pogName,
      domain: selectedDomain,
      description,
      enabled,
      permission_object_ids: selectedPOs,
    };

    if (isEditMode && pogId !== null) {
      onUpdate(pogId, payload);
    } else {
      onCreate(payload);
    }

    resetForm();
  };

  // -----------------------------------------------------
  // RESET
  // -----------------------------------------------------
  const resetForm = () => {
    setPogName("");
    setSelectedDomain("");
    setDescription("");
    setEnabled("Y");
    setSelectedPOs([]);
    setPogId(null);
    setIsEditMode(false);
    setIsViewMode(false);
    onDirtyChange(false);
    onModeChange(null);
    setModifiedFields({});
  };

  // Without admin the form is view-only: every field stays disabled.
  const fieldsLocked = !canDelete || (!isEditMode && isViewMode);

  const handleDeleteClick = () => {
    if (!pogId) return;
  
    setDialog({
      open: true,
      title: "Confirm Delete",
      message: "Are you sure you want to delete this POG?",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      onConfirm: () => {
        onDelete(pogId);
        closeDialog();
        resetForm();
      },
      onCancel: closeDialog,
    });
  };

  // -----------------------------------------------------
  // UI
  // -----------------------------------------------------
  return (
    <div className="bg-white p-6 rounded-lg shadow-md font-poppins">
      {dialog.open && (
        <Dialog
          title={dialog.title}
          message={dialog.message}
          confirmButtonText={dialog.confirmButtonText}
          onConfirm={dialog.onConfirm}
          onClose={closeDialog}
        />
      )}

      <form className="space-y-6">
      <div className="flex justify-between items-center gap-4 mb-4">

<div>
  {isEditMode && canDelete && (
    <button
      onClick={handleDeleteClick}
      type="button"
      className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white font-medium rounded-lg hover:bg-red-600"
    >
      <FaTrash className="w-4 h-4" />
      Delete
    </button>
  )}
</div>

<div className="flex gap-4">
{/* CREATE MODE */}
{canDelete && !isViewMode && !isEditMode && (
  <button
    onClick={handleSubmit}
    className="flex items-center gap-2 px-4 py-2 bg-[#a6a9ac] text-white rounded-lg hover:bg-[#474747]"
  >
    <FaPlus /> Create
  </button>
)}

{/* VIEW MODE */}
{canDelete && isViewMode && !isEditMode && (
  <>
    <button
      onClick={() => {
        resetForm();
        onModeChange("create");
      }}
      className="flex items-center gap-2 px-4 py-2 bg-[#a6a9ac] text-white rounded-lg hover:bg-[#474747]"
    >
      <FaPlus /> New
    </button>

    <button
      onClick={() => {
        setIsEditMode(true);
        setIsViewMode(false);
        onModeChange("edit");
      }}
      className="flex items-center gap-2 px-4 py-2 bg-[#a6a9ac] text-white rounded-lg hover:bg-[#474747]"
    >
      <FaEdit /> Edit
    </button>
  </>
)}

{/* EDIT MODE */}
{canDelete && isEditMode && (
  <>
    <button
      onClick={handleSubmit}
      className="flex items-center gap-2 px-4 py-2 bg-[#a6a9ac] text-white rounded-lg hover:bg-[#474747]"
    >
      <FaSave /> Save
    </button>

    <button
      onClick={() => {
        setIsEditMode(false);
        setIsViewMode(true);
        onDirtyChange(false);
      }}
      className="flex items-center gap-2 px-4 py-2 bg-[#a6a9ac] text-white rounded-lg hover:bg-[#474747]"
    >
      Cancel
    </button>
  </>
)}
</div>
</div>

        {/* ROW 1 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <label className="block text-gray-700 mb-2 font-medium">
              POG Name<span className="text-red-500 ml-1">*</span>
            </label>
            <input
              type="text"
              value={pogName}
              onChange={(e) => {
                setPogName(e.target.value);
                setModifiedFields(prev => ({ ...prev, pogName: true }));
                onDirtyChange(true);
              }}
              onFocus={() => setActiveField("pogName")}
              disabled={fieldsLocked}
              className={`w-full p-3 border rounded-lg text-sm transition-all duration-200
                ${fieldsLocked
                  ? "bg-[#f9f9f9] border-gray-300"
                  : isEditMode && (activeField === "pogName" || modifiedFields.pogName)
                  ? "border-green-500 focus:ring-2 focus:ring-green-500"
                  : "border-blue-500 focus:ring-2 focus:ring-blue-500"}`}
            />
          </div>

          <div>
            <label className="block text-gray-700 mb-2 font-medium">
              Domain
            </label>
            <DomainDropdown
              selectedDomain={selectedDomain}
              onDomainChange={(val: string) => {
                setSelectedDomain(val);
                setModifiedFields(prev => ({ ...prev, domain: true }));
                onDirtyChange(true);
              }}
              disabled={fieldsLocked}
              onFocus={() => setActiveField("domain")}
              className={`w-full p-1 border rounded-lg text-sm transition-all duration-200
                ${!isEditMode
                  ? "bg-[#f9f9f9] border-gray-300"
                  : activeField === "domain" || modifiedFields.domain
                  ? "border-green-500 focus:ring-2 focus:ring-green-500"
                  : "border-blue-500 focus:ring-2 focus:ring-blue-500"}`}
            />
          </div>
        </div>

        {/* ROW 2 */}
        <div>
          <label className="block text-gray-700 mb-2 font-medium">
            Description
          </label>
          <textarea
            rows={2}
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              setModifiedFields(prev => ({ ...prev, description: true }));
              onDirtyChange(true);
            }}
            onFocus={() => setActiveField("description")}
            disabled={fieldsLocked}
            className={`w-full p-3 border rounded-lg text-sm transition-all duration-200
              ${fieldsLocked
                ? "bg-[#f9f9f9] border-gray-300"
                : activeField === "description" || modifiedFields.description
                ? "border-green-500 focus:ring-2 focus:ring-green-500"
                : "border-blue-500 focus:ring-2 focus:ring-blue-500"}`}
          />
        </div>

        {/* ROW 3 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <label className="block text-gray-700 mb-2 font-medium">
              Enabled
            </label>
            <EnabledDropdown
              selectedEnabled={enabled}
              onEnabledChange={(val) => {
                setEnabled(val);
                setModifiedFields(prev => ({ ...prev, enabled: true }));
                onDirtyChange(true);
              }}
              disabled={fieldsLocked}
              onFocus={() => setActiveField("enabled")}
              className={`w-full p-1 border rounded-lg text-sm transition-all duration-200
                ${!isEditMode
                  ? "bg-[#f9f9f9] border-gray-300"
                  : activeField === "enabled" || modifiedFields.enabled
                  ? "border-green-500 focus:ring-2 focus:ring-green-500"
                  : "border-blue-500 focus:ring-2 focus:ring-blue-500"}`}
            />
          </div>
        </div>

        {/* PERMISSION OBJECTS */}
        <div>
          <label className="block text-gray-700 mb-2 font-medium">
            Select Permission Objects<span className="text-red-500 ml-1">*</span>
          </label>
          <div className="bg-gray-50 p-10 rounded-md border border-gray-300">
            <SearchableList
              items={permissionObjects.map(po => po.permission_object_name)}
              selectedItems={permissionObjects
                .filter(po => selectedPOs.includes(po.permission_object_id))
                .map(po => po.permission_object_name)}
              preserveItemOrder={false}
              reorderSelectedOnOutside={true}
              onSelectionChange={(names: string[]) => {
                const ids = permissionObjects
                  .filter(po => names.includes(po.permission_object_name))
                  .map(po => po.permission_object_id);

                setSelectedPOs(
                  keepHiddenSelections(
                    selectedPOs,
                    permissionObjects.map(po => po.permission_object_id),
                    ids
                  )
                );
                onDirtyChange(true);
              }}
              disabled={fieldsLocked}
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default PogForm;